/**
 * MlResultCard — COPILOT_MASTER_PLAN subsystem N.
 *
 * Renders a provenance-tagged tool result (ML / CV / data-query / MAS / info) as
 * a card IN the Companion message stream. Pure presentational — driven entirely
 * by the `ToolCard` descriptor the tool-registry produces, so it stays decoupled
 * from the window shell (the Agent panel / chat stream just maps cards → this).
 *
 * ADDITIVE: new file, no existing component touched.
 */

import { BrainCircuit, FileScan, Table2, Workflow, Info, Coins } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { ToolCard } from '@/lib/copilot/tools/types'

const KIND_ICON = {
  ml: BrainCircuit,
  cv: FileScan,
  query: Table2,
  mas: Workflow,
  info: Info,
} as const

const SEVERITY_KEYS = ['severity', 'level', 'risk_level', 'band', 'priority', 'niveau']
const PCT_HINTS = ['probability', 'confidence', 'score', 'rate', 'effectiveness', 'completeness', 'proba']

function isSeverityKey(k: string): boolean {
  return SEVERITY_KEYS.some((s) => k.toLowerCase().includes(s))
}
function severityVariant(v: string): 'default' | 'secondary' | 'destructive' {
  const s = v.toLowerCase()
  if (['critical', 'high', 'severe', 'p1', 'critique', 'élevé', 'eleve'].some((x) => s.includes(x))) return 'destructive'
  if (['medium', 'moderate', 'p2', 'moyen'].some((x) => s.includes(x))) return 'secondary'
  return 'default'
}
function formatScalar(key: string, value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non'
  if (typeof value === 'number') {
    const k = key.toLowerCase()
    if (PCT_HINTS.some((h) => k.includes(h)) && value >= 0 && value <= 1) return `${(value * 100).toFixed(1)}%`
    return Number.isInteger(value) ? String(value) : value.toFixed(3)
  }
  return String(value)
}
function labelize(k: string): string {
  return k.replace(/_/g, ' ')
}

export interface MlResultCardProps {
  card: ToolCard
  className?: string
}

export function MlResultCard({ card, className }: MlResultCardProps) {
  const Icon = KIND_ICON[card.kind] ?? Info
  const scalarRows = card.rows
    ? Object.entries(card.rows).filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object')
    : []
  const tableRows = card.table ?? []
  const tableCols = tableRows.length > 0 ? Object.keys(tableRows[0]) : []

  return (
    <div
      className={cn('mt-2 rounded-lg border bg-muted/30 p-3 space-y-2 overflow-hidden', className)}
      data-testid="ml-result-card"
      data-copilot-id="ml-result-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Icon className="h-3.5 w-3.5 text-primary shrink-0" />
          <span className="text-xs font-semibold truncate">{card.title}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {card.source && (
            <Badge
              variant={card.source === 'ml-inference' ? 'default' : 'secondary'}
              className="text-[9px] h-4 px-1"
              data-testid="ml-source"
            >
              {card.source}
            </Badge>
          )}
          {typeof card.credit === 'number' && (
            <Badge variant="outline" className="text-[9px] h-4 px-1 gap-0.5" title="Crédits IA consommés">
              <Coins className="h-2.5 w-2.5" />
              {card.credit}
            </Badge>
          )}
        </div>
      </div>

      {/* Summary */}
      {card.summary && <p className="text-[11px] text-muted-foreground whitespace-pre-wrap break-words">{card.summary}</p>}

      {/* Scalar rows (ML / CV) */}
      {scalarRows.length > 0 && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          {scalarRows.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between gap-2 text-xs min-w-0">
              <span className="text-muted-foreground truncate capitalize">{labelize(k)}</span>
              {isSeverityKey(k) && typeof v === 'string' ? (
                <Badge variant={severityVariant(v)} className="text-[9px] h-4 px-1">
                  {v}
                </Badge>
              ) : (
                <span className="font-medium tabular-nums text-right truncate">{formatScalar(k, v)}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tabular rows (query) */}
      {tableRows.length > 0 && tableCols.length > 0 && (
        <div className="overflow-x-auto -mx-1 px-1">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b">
                {tableCols.slice(0, 4).map((c) => (
                  <th key={c} className="px-1 py-1 text-left font-medium text-muted-foreground capitalize whitespace-nowrap">
                    {labelize(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.slice(0, 8).map((row, i) => (
                <tr key={i} className="border-b border-border/40 last:border-0">
                  {tableCols.slice(0, 4).map((c) => (
                    <td key={c} className="px-1 py-1 tabular-nums truncate max-w-[120px]">
                      {formatScalar(c, row[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {tableRows.length > 8 && (
            <p className="text-[10px] text-muted-foreground pt-1">+{tableRows.length - 8} ligne(s)…</p>
          )}
        </div>
      )}
    </div>
  )
}

export default MlResultCard
